import { forwardRef, type HTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';
import { PROVENANCE_PILL_CLASSES, type ProvenanceTier } from '@/config/colors';
import { cn } from '@/lib/utils';

/**
 * Closed badge tone vocabulary — adding a tone is a spec change, not a
 * call-site invention.
 */
export type BadgeTone = 'neutral' | 'success' | 'warning' | 'destructive' | 'info' | 'brand';

/**
 * Canonical pill shape, shared with feature components that wrap
 * `Badge`-styled markup (e.g. the ingest-source dropdown, which is a
 * control, not a badge).
 */
export const BADGE_BASE_CLASS =
  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium';

/**
 * Tone → class family. Status-meaningful tones map onto the design tokens
 * (success green / warning amber / destructive red / ring blue / brand
 * purple); neutral is the card surface with muted text.
 */
export const TONE_CLASSES: Record<BadgeTone, string> = {
  neutral: 'bg-card text-muted-foreground border-border',
  success: 'bg-success/10 text-success border-success/30',
  warning: 'bg-warning/10 text-warning border-warning/30',
  destructive: 'bg-destructive/10 text-destructive border-destructive/30',
  info: 'bg-ring/10 text-ring border-ring/30',
  brand: 'bg-brand/10 text-brand border-brand/30',
};

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** Closed tone set; defaults to `neutral`. */
  tone?: BadgeTone;
  /**
   * Provenance tier (Constitution §13.2). When set, overrides `tone` and
   * consumes the shared colors module's pill map — the tier→color mapping
   * stays defined in exactly one place.
   */
  provenance?: ProvenanceTier;
  /** Optional leading icon (e.g. the agent-attribution bot). */
  icon?: LucideIcon;
  /** Composed via `cn()` (tailwind-merge); per-instance sizing wins. */
  className?: string;
}

/**
 * The one badge. Canonical pill: rounded-full, border, tone-tinted
 * background, small medium-weight text, optional leading icon.
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { tone = 'neutral', provenance, icon: Icon, className, children, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      className={cn(
        BADGE_BASE_CLASS,
        provenance ? PROVENANCE_PILL_CLASSES[provenance] : TONE_CLASSES[tone],
        className,
      )}
      {...rest}
    >
      {Icon ? <Icon className="h-3 w-3 shrink-0" aria-hidden /> : null}
      {children}
    </span>
  );
});
